import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState, type KeyboardEvent } from "react";
import type { BorderValue } from "../../../util/src/blueprint/types";
import dico from "../../../util/src/language/dico";
import ColorPicker from "./ColorPicker";
import { EDITOR_PICKER_POPOVER_Z_INDEX } from "./editorPickerPopoverZIndex";

export type BorderConfig = BorderValue;

type BorderPickerProps = {
  value: BorderConfig;
  disabled?: boolean;
  onChange: (nextConfig: BorderConfig) => void;
};

type Side = "top" | "right" | "bottom" | "left";
type Scope = "all" | Side;
type BorderStyle = "solid" | "dashed" | "dotted" | "double" | "none";

type ParsedBorderSide = {
  width: number;
  style: BorderStyle;
  color: string;
};

const SIDES: Side[] = ["top", "right", "bottom", "left"];
const BORDER_STYLES: BorderStyle[] = [
  "solid",
  "dashed",
  "dotted",
  "double",
  "none",
];

const BORDER_DEFAULT_SIDE: ParsedBorderSide = {
  width: 0,
  style: "none",
  color: "#000000",
};

const BORDER_DEFAULT_CONFIG: BorderConfig = {
  border: {
    top: "0px none #000000",
    right: "0px none #000000",
    bottom: "0px none #000000",
    left: "0px none #000000",
  },
};

const normalizeColor = (value: string): string => {
  const next = value.trim();
  return next.length > 0 ? next : BORDER_DEFAULT_SIDE.color;
};

const parseBorderSide = (value: string): ParsedBorderSide => {
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "none") {
    return { ...BORDER_DEFAULT_SIDE, width: 0, style: "none" };
  }

  const match = trimmed.match(
    /^([0-9]*\.?[0-9]+)px\s+(solid|dashed|dotted|double|none)\s+(.+)$/i,
  );
  if (!match) {
    return BORDER_DEFAULT_SIDE;
  }

  const widthRaw = match[1];
  const styleRaw = match[2];
  const colorRaw = match[3];
  if (!widthRaw || !styleRaw || !colorRaw) {
    return BORDER_DEFAULT_SIDE;
  }

  return {
    width: Math.max(0, Number.parseFloat(widthRaw)),
    style: styleRaw.toLowerCase() as BorderStyle,
    color: normalizeColor(colorRaw),
  };
};

const stringifyBorderSide = (value: ParsedBorderSide): string => {
  const width = Number.isFinite(value.width) ? Math.max(0, value.width) : 0;
  return `${width}px ${value.style} ${normalizeColor(value.color)}`;
};

const stylePreviewSx: Record<
  BorderStyle,
  {
    borderTopStyle: "solid" | "dashed" | "dotted" | "double" | "none";
    borderTopWidth: number;
  }
> = {
  solid: { borderTopStyle: "solid", borderTopWidth: 3 },
  dashed: { borderTopStyle: "dashed", borderTopWidth: 3 },
  dotted: { borderTopStyle: "dotted", borderTopWidth: 3 },
  double: { borderTopStyle: "double", borderTopWidth: 4 },
  none: { borderTopStyle: "none", borderTopWidth: 3 },
};

/**
 * Renders a compact border editor with collapsed preview and popover controls.
 *
 * The component edits border sides using a scope-based UI inspired by the
 * property panel mockup.
 */
const BorderPicker: React.FC<BorderPickerProps> = ({
  value,
  disabled = false,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [scope, setScope] = useState<Scope>("all");
  const labels =
    (
      dico().licensedPortal?.layoutEditor as {
        borderPicker?: Record<string, string>;
      }
    )?.borderPicker ?? ({} as Record<string, string>);

  const mergedValue: BorderConfig = useMemo(
    () => ({
      border: {
        top: value?.border?.top ?? BORDER_DEFAULT_CONFIG.border.top,
        right: value?.border?.right ?? BORDER_DEFAULT_CONFIG.border.right,
        bottom: value?.border?.bottom ?? BORDER_DEFAULT_CONFIG.border.bottom,
        left: value?.border?.left ?? BORDER_DEFAULT_CONFIG.border.left,
      },
    }),
    [value],
  );

  const activeSide = scope === "all" ? "top" : scope;
  const activeBorder = parseBorderSide(mergedValue.border[activeSide]);
  const popupOpen = Boolean(anchorEl);

  const updateBorder = (partial: Partial<ParsedBorderSide>) => {
    const nextConfig: BorderConfig = {
      border: { ...mergedValue.border },
    };
    const sides = scope === "all" ? SIDES : [scope];

    sides.forEach((side) => {
      const current = parseBorderSide(mergedValue.border[side]);
      nextConfig.border[side] = stringifyBorderSide({ ...current, ...partial });
    });

    onChange(nextConfig);
  };

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={(event) => {
          if (!disabled) {
            setAnchorEl(event.currentTarget as HTMLElement);
          }
        }}
        onKeyDown={(event) => {
          if (!disabled && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            setAnchorEl(event.currentTarget as HTMLElement);
          }
        }}
        sx={{
          border: "1px solid #d8e0eb",
          borderRadius: "8px",
          backgroundColor: "#fbfcfe",
          p: "3px 8px",
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.55 : 1,
          "&:hover": {
            borderColor: disabled ? "#d8e0eb" : "#94a3b8",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: 30,
            borderRadius: 1.5,
            background: "#fff",
            borderTop: mergedValue.border.top,
            borderRight: mergedValue.border.right,
            borderBottom: mergedValue.border.bottom,
            borderLeft: mergedValue.border.left,
          }}
        />
      </Box>

      <Popover
        open={popupOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          root: {
            sx: { zIndex: EDITOR_PICKER_POPOVER_Z_INDEX },
          },
        }}
      >
        <Box
          sx={{
            width: 320,
            background: "#fff",
            borderRadius: "12px",
            p: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#1f2937",
              }}
            >
              {labels.title ?? ""}
            </Typography>
            <Tooltip title={labels.reset ?? ""}>
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => onChange(BORDER_DEFAULT_CONFIG)}
                >
                  <RestartAltIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={sectionLabelSx}>
              {labels.scopeLabel ?? ""}
            </Typography>
            <Box sx={toggleGroupSx}>
              {(["all", ...SIDES] as Scope[]).map((entry) => (
                <Box
                  key={entry}
                  role="button"
                  tabIndex={0}
                  onClick={() => setScope(entry)}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setScope(entry);
                    }
                  }}
                  sx={[chipSx, scope === entry ? chipActiveSx : null]}
                >
                  {entry === "all"
                    ? (labels.scopeAll ?? "")
                    : (labels[
                        `scope${entry.charAt(0).toUpperCase()}${entry.slice(1)}`
                      ] ?? "")}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={sectionLabelSx}>
              {labels.styleLabel ?? ""}
            </Typography>
            <Box sx={styleOptionsSx}>
              {BORDER_STYLES.map((style) => (
                <Tooltip
                  key={style}
                  title={
                    labels[
                      `style${style.charAt(0).toUpperCase()}${style.slice(1)}`
                    ] ?? ""
                  }
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => updateBorder({ style })}
                    onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        updateBorder({ style });
                      }
                    }}
                    sx={[
                      chipSx,
                      { height: 36, borderRadius: "6px" },
                      activeBorder.style === style ? chipActiveSx : null,
                    ]}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: 0,
                        borderTopColor: "currentColor",
                        borderTopStyle: stylePreviewSx[style].borderTopStyle,
                        borderTopWidth: stylePreviewSx[style].borderTopWidth,
                      }}
                    />
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box>
              <Typography sx={sectionLabelSx}>
                {labels.widthLabel ?? ""}
              </Typography>
              <TextField
                size="small"
                type="number"
                fullWidth
                value={Math.round(activeBorder.width)}
                onChange={(event) =>
                  updateBorder({ width: Number(event.target.value) || 0 })
                }
                sx={inputSx}
                slotProps={{ htmlInput: { min: 0, step: 1 } }}
              />
            </Box>
          </Box>

          <Box>
            <Typography sx={sectionLabelSx}>
              {labels.colorLabel ?? ""}
            </Typography>
            <ColorPicker
              value={activeBorder.color}
              disabled={disabled}
              onChange={(nextColor) => updateBorder({ color: nextColor })}
            />
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const sectionLabelSx = {
  display: "block",
  fontSize: 12,
  color: "#64748b",
  mb: 0.75,
};

const toggleGroupSx = {
  display: "flex",
  gap: "5px",
  flexWrap: "wrap",
};

const styleOptionsSx = {
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  gap: "6px",
};

const chipSx = {
  minWidth: 0,
  height: 28,
  padding: "0 10px",
  borderRadius: "6px",
  border: "1px solid #c7d2e0",
  background: "#ffffff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 12,
  color: "#334155",
  cursor: "pointer",
  userSelect: "none",
  "&:hover": {
    borderColor: "#94a3b8",
    background: "#f1f5f9",
  },
};

const chipActiveSx = {
  borderColor: "#4f9cff",
  color: "#0f3f79",
  fontWeight: 600,
  background: "rgba(79, 156, 255, 0.18)",
};

const inputSx = {
  "& .MuiInputBase-root": {
    height: 42,
    borderRadius: "6px",
    "& input": {
      fontSize: 14,
    },
  },
};

export default BorderPicker;
